import { useCallback } from 'react';
import { regionsApi } from '../api/regions.api';
import type { Region, RegionSelector } from '../types/regions.types';
import { REGION_HIERARCHY_QUERY_PREFIX } from './RegionHierarchyInput';
import { HierarchyMultiSelect } from '@/shared/components/ahkinia/HierarchyMultiSelect';
import type {
  HierarchyFetchParams,
  HierarchyMultiSelectProps,
} from '@/shared/components/ahkinia/HierarchyMultiSelect';

interface RegionHierarchyMultiSelectProps extends Omit<
  HierarchyMultiSelectProps<Region>,
  'fetchItems' | 'fetchItemById' | 'getLabel' | 'getParent' | 'queryKey'
> {
  countryIds: string[];
  selector: RegionSelector;
  /** Id to exclude from the candidate list — pass the region being edited so it can't become its own child. */
  excludeId?: string;
  /** When true (default), selecting a region also selects all its descendants. */
  cascading?: boolean;
}

export function RegionHierarchyMultiSelect({
  countryIds,
  selector,
  excludeId,
  cascading = true,
  ...props
}: RegionHierarchyMultiSelectProps) {
  const countryIdsKey = countryIds.join(',');

  const fetchItems = useCallback(
    async ({ parentId, page = 1, pageSize = 100 }: HierarchyFetchParams) => {
      const result = await regionsApi.getRegions({
        countryIds,
        selector,
        parentId,
        page,
        pageSize,
        // Una region puede colgar de cualquier nivel del arbol: el servidor amplia countryIds
        // con ancestros y descendientes en una sola consulta.
        expandCountryHierarchy: true,
      });
      const items = result.data?.items ?? [];
      return {
        items: excludeId ? items.filter((item) => item.id !== excludeId) : items,
        hasMore: result.data?.hasNextPage ?? false,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- countryIds is a plain array re-created by callers on every render; countryIdsKey (its joined value) is the real identity for memoization here.
    [countryIdsKey, selector, excludeId],
  );

  const fetchItemById = useCallback(async (id: string) => {
    const result = await regionsApi.getRegionById(id);
    return result.data as Region;
  }, []);

  return (
    <HierarchyMultiSelect<Region>
      {...props}
      cascading={cascading}
      fetchItems={fetchItems}
      fetchItemById={fetchItemById}
      getLabel={(r) => r.name}
      getParent={(r) => r.parentId}
      queryKey={`${REGION_HIERARCHY_QUERY_PREFIX}${countryIdsKey}-${selector}`}
    />
  );
}
